<template>
  <header class="flex-shrink-0 relative h-12 bg-white flex items-center border-b-2 border-primary-lightest">
    <div class="absolute inset-y-0 left-0 z-10">
      <div
        class="hidden md:block h-28 w-24 rounded-b-3xl"
        :class="[{
          'bg-gradient-to-r from-primary-dark to-primary': !groupingLogo,
          'bg-white shadow cursor-pointer': groupingLogo
        }]"
        @click="redirectToGrouping"
      >
        <div class="flex items-center justify-center h-28">
          <img
            v-if="!detecting"
            class="h-12 w-auto"
            :src="groupingLogo ? groupingLogo : apodisLogoWhite"
            alt="Apodis"
          >
          <BaseSpinner
            v-else
            :color="groupingLogo ? 'primary' : 'white'"
            size="medium-small"
          />
        </div>
      </div>
    </div>

    <div class="flex flex-1 justify-between min-w-0 gap-4">
      <div class="text-primary md:ml-28 sm:ml-4 font-semibold flex items-center min-w-0 flex-1">
        <TheBackButton class="!p-0 flex-shrink-0" />
        <span class="truncate">{{ title }}</span>
      </div>

      <div class="hidden md:flex md:items-center md:flex-shrink-0">
        <div class="pr-4 flex-shrink-0 flex items-center space-x-10">
          <div class="flex items-center space-x-3">
            <ApodisPremiumButton />
            <VersionButton />
            <CreditBalance />
            <ApodisCoinBalance />
            <div class="relative flex flex-row space-x-3 align-center text-left">
              <router-link :to="{ name: 'notification.history' }" class="button-top" active-class="button-active">
                <span class="sr-only">Notifications</span>
                <span class="relative inline-block">
                  <Icon name="mdi-bell" class="text-primary" />
                  <span
                    v-if="notificationCountLabel !== null"
                    class="absolute top-2 right-2.5 h-4 w-4 inline-flex items-center justify-center px-2 py-1 text-sm font-bold leading-none text-white transform translate-x-1/2 -translate-y-1/2 bg-red-600 rounded-full"
                  >{{ notificationCountLabel }}</span>
                </span>
              </router-link>
              <router-link :to="{ name: 'agenda' }" class="button-top" active-class="button-active">
                <span class="sr-only">Agenda</span>
                <Icon name="mdi-calendar-month" class="text-primary" />
              </router-link>
              <TheUserMenu />
            </div>
          </div>
        </div>
      </div>
    </div>

    <TheMobileMenu />
  </header>
</template>

<script setup lang="ts">
import {computed, onMounted, ref} from "vue";
import TheUserMenu from '@/core/components/navigation-bar/TheUserMenu.vue';
import TheMobileMenu from '@/core/components/navigation-bar/TheMobileMenu.vue';
import BaseSpinner from '@/core/components/base/spinner/BaseSpinner.vue';
import VersionButton from '@/core/components/version-button/VersionButton.vue';
import {useSystemStore} from '@/stores/system.store';
import ApodisPremiumButton from "@/core/components/apodis-premium/ApodisPremiumButton.vue";
import {useRoute, useRouter} from "vue-router";
import {storeToRefs} from "pinia";
import apodisLogoWhite from '@/assets/images/logos/apodis-white.svg';
import {usePatientStore} from "@/stores/patient.store";
import {NotificationEventParams} from "@/core/interfaces/Event";
import Icon from "@/core/components/icon/Icon.vue";
import ApodisCoinBalance from "@/core/components/navigation-bar/ApodisCoinBalance.vue";
import TheBackButton from "@/core/components/navigation-bar/TheBackButton.vue";
import CreditBalance from "@/core/components/navigation-bar/CreditBalance.vue";
import {extensionApi} from "@/container";
import ExtensionNavigationService from "@/extension/services/ExtensionNavigationService";

const {notificationCount, environment, groupingLogo} = storeToRefs(useSystemStore());
const patientStore = usePatientStore();

const router = useRouter();
const route = useRoute();
const detecting = ref(false);

const title = computed(() => {
    if (groupingLogo.value) {
        return route.meta.title || environment.value?.entity?.grouping?.name;
    }

    return route.meta.title || 'Apodis Connect';
});

const notificationCountLabel = computed(() => {
    const count = notificationCount.value;

    if (!count) {
        return null;
    }

    return count > 9 ? '9+' : count;
});

/**
 * Show a support program.
 */
const showSupportProgram = (event: never, params: NotificationEventParams) => {
    patientStore
        .load(params.customerId)
        .then(() => router.push({
            name: 'consumer.support-program',
            params: {customerId: params.customerId, programId: params.programId, consumerKey: params.consumerKey}
        }));
};

/**
 * Show a customer, or one of their beneficiaries when a beneficiary id is given.
 */
const showCustomer = (event: never, customerId: number, query: Record<string, string> = {}, beneficiaryId?: number) => {
    patientStore
        .load(customerId)
        .then(() => router.push(beneficiaryId
            ? {name: 'customer.beneficiary', params: {customerId, id: beneficiaryId}, query}
            : {name: 'customer.home', params: {customerId}, query}
        ));
};

/**
 * Show a module, from the popup: where it stands decides where it opens — its
 * store sheet to activate it, its configuration, or the module itself. A
 * module the pharmacy is not offered, or one that cannot be read, opens the
 * store.
 */
const showExtension = async (event: never, extensionId: number | null) => {
    if (!extensionId) {
        await router.push({name: 'extension-store'});

        return;
    }

    try {
        ExtensionNavigationService.navigate(await extensionApi().read(extensionId), router, route);
    } catch {
        await router.push({name: 'extension-store'});
    }
};

const redirectToGrouping = () => {
    if (groupingLogo.value) {
        router.push({name: 'grouping'});
    }
};

onMounted(() => {
    window.ipcRenderer?.on('detection-start', () => {
        detecting.value = true;
    });

    window.ipcRenderer?.on('detection-completed', () => {
        detecting.value = false;
    });


    window.ipcRenderer?.off('show-support-program', showSupportProgram);
    window.ipcRenderer?.on('show-support-program', showSupportProgram);

    window.ipcRenderer?.off('show-customer', showCustomer);
    window.ipcRenderer?.on('show-customer', showCustomer);

    window.ipcRenderer?.off('show-extension', showExtension);
    window.ipcRenderer?.on('show-extension', showExtension);

});
</script>

<style scoped>


.button:hover,
.button-active span > svg {
    @apply stroke-current text-secondary;
}
</style>
